import React, { useState, useMemo } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Search, Calendar, X } from "lucide-react";

export interface HubSpotForm {
  id: string;
  name: string;
  createdAt: string;
  portalId?: string;
  action?: string;
  method?: string;
}

export interface FormSelectionProps {
  forms: HubSpotForm[];
  selectedForms: Set<string>;
  onFormToggle: (formId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  loading?: boolean;
}

/**
 * Enhanced FormSelection component with condensed display, search, and year filtering.
 * Displays forms without POST and Portal information as requested.
 */
export const FormSelection: React.FC<FormSelectionProps> = ({
  forms,
  selectedForms,
  onFormToggle,
  onSelectAll,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState<string>("all");

  // Extract unique years from forms
  const availableYears = useMemo(() => {
    const years = forms
      .map((form) => new Date(form.createdAt).getFullYear())
      .filter((year) => !isNaN(year))
      .sort((a, b) => b - a); // Sort descending (newest first)

    return [...new Set(years)];
  }, [forms]);

  // Filter forms based on search term and selected year
  const filteredForms = useMemo(() => {
    let filtered = forms;

    // Filter by search term (name and ID)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (form) =>
          form.name.toLowerCase().includes(term) ||
          form.id.toLowerCase().includes(term)
      );
    }

    // Filter by year
    if (selectedYear !== "all") {
      const year = parseInt(selectedYear);
      filtered = filtered.filter(
        (form) => new Date(form.createdAt).getFullYear() === year
      );
    }

    return filtered;
  }, [forms, searchTerm, selectedYear]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedYear("all");
  };

  const hasActiveFilters = searchTerm.trim() !== "" || selectedYear !== "all";

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-muted-foreground">Loading forms...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by form name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          <Select
            value={selectedYear}
            onValueChange={setSelectedYear}
            className="w-[150px]"
          >
            <SelectTrigger className="w-[150px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {availableYears.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="px-3"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Selection Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all-filtered"
            checked={
              filteredForms.length > 0 &&
              filteredForms.every((form) => selectedForms.has(form.id))
            }
            onCheckedChange={(checked) => {
              if (checked) {
                filteredForms.forEach((form) => onFormToggle(form.id, true));
              } else {
                filteredForms.forEach((form) => onFormToggle(form.id, false));
              }
            }}
            disabled={filteredForms.length === 0}
          />
          <label
            htmlFor="select-all-filtered"
            className="text-sm font-medium cursor-pointer"
          >
            Select All Filtered ({filteredForms.length})
          </label>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary">
            {selectedForms.size} selected
          </Badge>
          {hasActiveFilters && (
            <span>
              Showing {filteredForms.length} of {forms.length} forms
            </span>
          )}
        </div>
      </div>

      {/* Forms List - Condensed Display */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredForms.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {hasActiveFilters
              ? "No forms match your search criteria."
              : "No forms available."}
          </div>
        ) : (
          filteredForms.map((form) => {
            const isSelected = selectedForms.has(form.id);
            return (
              <Card
                key={form.id}
                className={`transition-all cursor-pointer hover:shadow-sm border ${
                  isSelected
                    ? "ring-1 ring-primary bg-primary/5 border-primary/20"
                    : "hover:border-border"
                }`}
                onClick={() => onFormToggle(form.id, !isSelected)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) =>
                        onFormToggle(form.id, !!checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 mr-3">
                          <h4 className="font-medium text-sm truncate">
                            {form.name}
                          </h4>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span className="font-mono bg-muted px-1.5 py-0.5 rounded">
                              {form.id}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(form.createdAt)}
                            </span>
                          </div>
                        </div>

                        {/* Action URL - only show if present and different from default */}
                        {form.action && form.action !== "/" && (
                          <div className="text-xs text-muted-foreground max-w-xs">
                            <code className="bg-muted px-1 py-0.5 rounded text-xs truncate block">
                              {form.action.length > 40
                                ? `${form.action.substring(0, 40)}...`
                                : form.action}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FormSelection;